/**
 * @file build-from-card.ts
 * @description One mapping table from `ActionCard` → typed `Intent`. Replaces
 * the deleted `build-intent-hint.ts`. Used by `build-card-command.ts` (slash
 * command authoring) and the action-card click path (`triggerCardShortcut`).
 *
 * The Rust enricher fills mechanics — weapon stats, attack bonus, save DC,
 * full SpellData — from the canonical inventory/spellbook lookup. The FE
 * only ships identifying names + target IDs.
 *
 * Mapping is keyed strictly on `card.activation.kind`. See the design doc
 * `docs/game-engine/TYPED_INTENTS.md` for context.
 */

import type {
  ActionCard,
  ActionCost,
  ActionSlot,
  AttackSpec,
  DamageType,
  Intent,
  IntentAttack,
  IntentCastSpell,
  IntentDash,
  IntentDisengage,
  IntentDodge,
  IntentDropProne,
  IntentEndConcentration,
  IntentEscapeGrapple,
  IntentHelp,
  IntentHide,
  IntentSearch,
  IntentStabilize,
  IntentStandUp,
  IntentSteadyAim,
  IntentStudy,
  IntentUseAbility,
  IntentUseItem,
  IntentWait,
  ResourceRequirement,
  SpellTarget,
  TurnFlags,
  UsableItem,
} from '@danda/shared';

/** Build result — the FE-direct wire envelope. `action_slot` mirrors the
 *  card's `availability.action_cost`; `turn_flags` carries any turn-level
 *  modifier the click declares (currently only Reckless Attack).
 *
 *  `optimistic` carries the click's `resource_requirement` + display label so
 *  the send site can pre-decrement the matching counter (#214). The
 *  authoritative `my_character_session` snapshot precedence in
 *  `syncPlayerCharData` reconciles on the next round_result — counter rolls
 *  back automatically on engine rejection.
 *
 *  `itemCharge` carries the magic-item id + charges so the send site can
 *  decrement the item's `charges` field on `use_item` clicks (charge-based
 *  magic items like Wand of Magic Missiles).
 */
export interface BuiltIntent {
  intent: Intent;
  action_slot: ActionSlot;
  turn_flags?: TurnFlags;
  optimistic?: {
    requirement: ResourceRequirement;
    cardLabel: string;
  };
  itemCharge?: {
    item_id: string;
    count: number;
  };
}

/** Map an `ActionCost` (action-card domain) to the wire-format `ActionSlot`.
 *  `'post_attack'` and `'on_spell'` are post-resolution riders that the engine
 *  treats as `Free` for slot accounting; `'none'` is a no-cost intent. */
export function actionCostToSlot(cost: ActionCost): ActionSlot {
  switch (cost) {
    case 'action':
      return 'action';
    case 'bonus_action':
      return 'bonus_action';
    case 'reaction':
      return 'reaction';
    case 'free':
    case 'post_attack':
    case 'on_spell':
    case 'none':
      return 'free';
  }
}

export interface BuildIntentFromCardContext {
  /** Selected target id(s) from the target picker (if any). */
  targetIds?: string[];
  /** Selected mode_id from the modal picker (if any). */
  modeId?: string;
  /** Toggle state override (explicit activate / deactivate for toggle cards). */
  activate?: boolean;
  /** Pool spend override (Lay on Hands amount picker). */
  poolSpend?: number;
  /** Selected transmuted damage type (for Transmuted Spell metamagic). */
  transmutedDamageType?: DamageType;
}

/**
 * Build a typed `Intent` from an `ActionCard` click, wrapped in the wire
 * envelope (`{ intent, action_slot, turn_flags? }`).
 *
 * Returns `null` when the card cannot produce a complete Intent without
 * additional selection state (e.g. no target picked yet) — callers should
 * either continue the selection flow or fall back to free text.
 *
 * The action_slot is derived from `card.availability.action_cost`. Reckless
 * Attack and other turn-level modifier cards ship via `turn_flags`.
 */
export function buildIntentFromCard(
  card: ActionCard,
  ctx: BuildIntentFromCardContext = {}
): BuiltIntent | null {
  const targetIds = ctx.targetIds ?? [];
  const primaryTarget = targetIds[0];
  const cardSlot = actionCostToSlot(card.availability.action_cost);

  // Build the optimistic-decrement payload from the card's resource
  // requirement. Skipped when the card has no requirement (free-cost
  // ribbons like Dodge), or when the resource is `charge` (handled via
  // itemCharge instead). Lay-on-Hands pool spend uses the resolved
  // poolSpend (amount picker) so the optimistic decrement matches the
  // server delta exactly.
  const buildOptimistic = (): BuiltIntent['optimistic'] | undefined => {
    const req = card.availability.resource_requirement;
    if (!req || req.resource === 'charge') return undefined;
    if (req.resource === 'lay_on_hands_pool' && typeof ctx.poolSpend === 'number') {
      return {
        requirement: { ...req, amount: Math.max(1, ctx.poolSpend) },
        cardLabel: card.label,
      };
    }
    return { requirement: req, cardLabel: card.label };
  };

  switch (card.activation.kind) {
    case 'attack_with_flurry': {
      // Flurry of Blows — N unarmed strikes, one per target picked. Emit a
      // stub Intent (with empty `attacks`) when no target was picked at card-
      // construction time so `buildIntent`'s merge layer recognises this as a
      // flurry-card-derived flow and fills in `attacks` from the selection
      // step's target id. The engine expands a single FE-direct strike to
      // the SRD-mandated 2 (or 3 at L10+) when `flurry_of_blows: true`.
      const attacks: AttackSpec[] = targetIds.map((target_id) => ({
        variant: { kind: 'unarmed_strike', option_type: 'damage', target_id },
      }));
      const intent: IntentAttack = {
        intent_type: 'attack',
        attacks,
        flurry_of_blows: true,
      };
      // Flurry MUST ship as a bonus action — the engine's Flurry validation
      // gate fires on `action_slot == BonusAction`. Override the card's
      // action_cost defensively even though it should already be
      // `bonus_action`.
      return { intent, action_slot: 'bonus_action', optimistic: buildOptimistic() };
    }

    case 'use_ability': {
      const intent: IntentUseAbility = {
        intent_type: 'use_ability',
        ability_name: card.activation.ability_name,
        target_ids: [...targetIds],
        // `activate` defaults follow the previous hint behaviour: explicit
        // override wins, otherwise toggle cards default to `true` so a click
        // on a toggle unambiguously activates.
        activate:
          ctx.activate !== undefined ? ctx.activate : card.activation.toggle ? true : undefined,
        mode_id: ctx.modeId,
        pool_spend: ctx.poolSpend,
      };
      // Toggle cards that are deactivating shouldn't decrement the counter —
      // turning Rage off doesn't spend another use. For non-toggle abilities
      // and toggle activations, decrement.
      const deactivating = card.activation.toggle && ctx.activate === false;
      return {
        intent,
        action_slot: cardSlot,
        optimistic: deactivating ? undefined : buildOptimistic(),
      };
    }

    case 'cast_spell_from_source': {
      // Free cast (Magic Initiate, species lineage). Enricher honours
      // free_cast and skips slot consumption + spell-list checks.
      const targets: SpellTarget[] = targetIds.map((target_id) => ({
        target_type: 'character',
        character_id: target_id,
      }));
      const intent: IntentCastSpell = {
        intent_type: 'cast_spell',
        spell_name: card.activation.spell_name,
        // Free casts always at base level (slot_level: 0 means "no slot
        // consumed"). Enricher fills the actual cast level from spell data.
        slot_level: 0,
        targets,
        free_cast: true,
      };
      return { intent, action_slot: cardSlot, optimistic: buildOptimistic() };
    }

    case 'spell_cast_with_metamagic': {
      // Metamagic card is a spell modifier — the actual spell + targets come
      // from the slash-flow's spell + target steps. At card-construction time
      // we don't know either, so emit a stub Intent that the
      // `buildIntent` merger fills with selection-derived fields.
      const targets: SpellTarget[] = targetIds.map((target_id) => ({
        target_type: 'character',
        character_id: target_id,
      }));
      // Transmuted Spell — caster picks Acid/Cold/Fire/Lightning/Poison/
      // Thunder via the damage-type picker before the spell pick. When ctx
      // doesn't carry a pick (legacy click-fast paths, programmatic
      // shortcuts that skip the picker), default to Fire so the stub stays
      // valid and the enricher's "transmuted_damage_type required" gate
      // passes.
      const transmutedDamageType =
        card.activation.option === 'transmuted_spell'
          ? (ctx.transmutedDamageType ?? 'fire')
          : undefined;
      const intent: IntentCastSpell = {
        intent_type: 'cast_spell',
        spell_name: '',
        slot_level: 0,
        targets,
        metamagic: [card.activation.option],
        transmuted_damage_type: transmutedDamageType,
      };
      // Metamagic cards always declare a sorcery_points cost via
      // resource_requirement — pre-decrement so the SP counter drops
      // immediately on click.
      return { intent, action_slot: cardSlot, optimistic: buildOptimistic() };
    }

    case 'use_item': {
      // Ship the item's display name + canonical id; the enricher resolves
      // the canonical magic item from `item_id` (collision-safe), falling
      // back to name lookup. The wire `UsableItem::MagicItem` variant
      // carries an explicit `item_id: Option<ItemId>` for disambiguation.
      const item: UsableItem = {
        item_type: 'magic_item',
        item_name: card.label,
        charges_used: card.activation.charges,
        spell_choice: card.activation.spell_choice,
        ability_choice: card.activation.ability_choice,
        item_id: card.activation.item_id,
      };
      const intent: IntentUseItem = {
        intent_type: 'use_item',
        item,
        target_ids: targetIds.length > 0 ? [...targetIds] : undefined,
      };
      // Magic-item charges decrement on click — the card carries the count.
      // Falls through whether or not the card has a `charge` resource
      // requirement; the item_id is the canonical key.
      const itemCharge =
        card.activation.item_id && card.activation.charges && card.activation.charges > 0
          ? { item_id: card.activation.item_id, count: card.activation.charges }
          : undefined;
      return { intent, action_slot: cardSlot, itemCharge };
    }

    case 'first_class_intent': {
      // The card carries the variant directly — emit the matching Intent.
      // First-class intents (Dodge, Disengage, Dash, Hide, Search, etc.) are
      // typically free-cost ribbons without resource counters, but pass
      // `buildOptimistic()` through the envelope anyway so any future card
      // that adds a counter (e.g. a Lucky-feat ribbon) gets the decrement
      // automatically.
      const opt = buildOptimistic();
      switch (card.activation.intent) {
        case 'dodge':
          return {
            intent: { intent_type: 'dodge' } satisfies IntentDodge,
            action_slot: cardSlot,
            optimistic: opt,
          };
        case 'disengage':
          return {
            intent: { intent_type: 'disengage' } satisfies IntentDisengage,
            action_slot: cardSlot,
            optimistic: opt,
          };
        case 'dash':
          return {
            intent: { intent_type: 'dash' } satisfies IntentDash,
            action_slot: cardSlot,
            optimistic: opt,
          };
        case 'hide':
          return {
            intent: { intent_type: 'hide' } satisfies IntentHide,
            action_slot: cardSlot,
            optimistic: opt,
          };
        case 'search':
          return {
            intent: { intent_type: 'search' } satisfies IntentSearch,
            action_slot: cardSlot,
            optimistic: opt,
          };
        case 'help': {
          if (!primaryTarget) return null;
          return {
            intent: { intent_type: 'help', target_id: primaryTarget } satisfies IntentHelp,
            action_slot: cardSlot,
            optimistic: opt,
          };
        }
        case 'study': {
          if (!primaryTarget) return null;
          return {
            intent: { intent_type: 'study', target_id: primaryTarget } satisfies IntentStudy,
            action_slot: cardSlot,
            optimistic: opt,
          };
        }
        case 'influence': {
          // InfluenceAction needs skill + bonuses + save DC — none of which
          // the card carries. Influence is a slash-only path today; the card
          // route is unreachable for it. Defensively return null.
          return null;
        }
        case 'drop_prone':
          return {
            intent: { intent_type: 'drop_prone' } satisfies IntentDropProne,
            action_slot: cardSlot,
            optimistic: opt,
          };
        case 'stand_up':
          return {
            intent: { intent_type: 'stand_up' } satisfies IntentStandUp,
            action_slot: cardSlot,
            optimistic: opt,
          };
        case 'stabilize': {
          if (!primaryTarget) return null;
          return {
            intent: {
              intent_type: 'stabilize',
              target_id: primaryTarget,
            } satisfies IntentStabilize,
            action_slot: cardSlot,
            optimistic: opt,
          };
        }
        case 'escape_grapple': {
          if (!primaryTarget) return null;
          return {
            intent: {
              intent_type: 'escape_grapple',
              grappler_id: primaryTarget,
            } satisfies IntentEscapeGrapple,
            action_slot: cardSlot,
            optimistic: opt,
          };
        }
        case 'end_concentration':
          return {
            intent: { intent_type: 'end_concentration' } satisfies IntentEndConcentration,
            action_slot: cardSlot,
            optimistic: opt,
          };
        case 'steady_aim':
          return {
            intent: { intent_type: 'steady_aim' } satisfies IntentSteadyAim,
            action_slot: cardSlot,
            optimistic: opt,
          };
        case 'reckless_attack': {
          // Reckless Attack is a turn-level flag — ride `turn_flags.reckless_attack`
          // on the wire. The card click is a no-op intent (Wait); the backend's
          // flag-only short-circuit emits a bare `CharIntent { reckless_attack:
          // true }` which the modifier-only collector folds onto the next
          // attack. Slot is `free` so the flag-only intent doesn't burn an
          // action.
          const intent: IntentWait = { intent_type: 'wait' };
          return {
            intent,
            action_slot: 'free',
            turn_flags: { reckless_attack: true },
          };
        }
        case 'action_surge': {
          // Action Surge is a turn-level flag — ride `turn_flags.action_surge`.
          // SRD 5.2.1: "On your turn, you can take one additional action,
          // except the Magic action." No action cost. Click ships an empty
          // Wait intent + flag; server-side, `handle_player_intent` validates
          // (Fighter L2+, has uses), the engine hook grants the extra action,
          // and `CharIntent::merge_with` routes the next Attack click into
          // `action_surge_action`.
          const intent: IntentWait = { intent_type: 'wait' };
          return {
            intent,
            action_slot: 'free',
            turn_flags: { action_surge: true },
          };
        }
        default: {
          // Exhaustiveness check — TS surfaces missed FirstClassIntentKind
          // additions here.
          const _exhaustive: never = card.activation.intent;
          void _exhaustive;
          return null;
        }
      }
    }
  }
}

/**
 * Derive the natural-language fill text for a card click. Used as the
 * chat-bubble display string. The typed Intent above carries the structured
 * data; this function only governs how the click reads in chat.
 */
export function buildFillText(card: ActionCard, ctx: BuildIntentFromCardContext = {}): string {
  const { targetIds, modeId } = ctx;
  const target = targetIds?.[0];

  switch (card.activation.kind) {
    case 'attack_with_flurry':
      return target ? `Attack ${target} with Flurry of Blows` : 'Use Flurry of Blows';
    case 'use_ability': {
      const modeSuffix = modeId ? ` (${modeId})` : '';
      if (target) return `Use ${card.label}${modeSuffix} on ${target}`;
      return `Use ${card.label}${modeSuffix}`;
    }
    case 'spell_cast_with_metamagic':
      return `Cast with ${card.label}`;
    case 'first_class_intent':
      return target ? `${card.label} ${target}` : card.label;
    case 'use_item':
      return target ? `Use ${card.label} on ${target}` : `Use ${card.label}`;
    case 'cast_spell_from_source':
      return target
        ? `Cast ${card.activation.spell_name} on ${target}`
        : `Cast ${card.activation.spell_name}`;
  }
}
